// eslint-disable-next-line no-restricted-imports
import { DateTime, Settings } from "luxon";
import i18n from "src/i18n/config";

Settings.defaultZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

class TimeService {
    private static readonly dateTimeFormat = "yyyy-MM-dd - HH:mm:ss";
    private static readonly dateFormat = "yyyy-MM-dd";

    private static getInMillis<T extends DateTime | string | number>(date: T, zone?: string): DateTime {
        if (typeof date === "number") {
            return this.getFromMillis(date, zone);
        } else if (typeof date === "string") {
            return this.getFromISO(date, zone);
        }
        return zone ? date.setZone(zone) : date;
    }

    private static getHumanReadableDifference(startDate: DateTime, endDate: DateTime): string | null {
        const [start, end] = startDate > endDate ? [endDate, startDate] : [startDate, endDate];
        const diff = end.diff(start, ["years", "months", "days", "hours", "minutes", "seconds"]).toObject();

        // normalize undefined values to 0 and round them upfront
        // we truncate seconds on purpose to avoid millis
        const normalizedDiff = {
            years: Math.round(diff.years ?? 0),
            months: Math.round(diff.months ?? 0),
            days: Math.round(diff.days ?? 0),
            hours: Math.round(diff.hours ?? 0),
            minutes: Math.round(diff.minutes ?? 0),
            seconds: Math.trunc(diff.seconds ?? 0),
        };

        // always translate all keys so static analyzers like "i18next-parser" can detect them
        const units = [
            {
                value: normalizedDiff.years,
                translated: i18n.t("common:services.time.year", { count: normalizedDiff.years }),
            },
            {
                value: normalizedDiff.months,
                translated: i18n.t("common:services.time.month", { count: normalizedDiff.months }),
            },
            {
                value: normalizedDiff.days,
                translated: i18n.t("common:services.time.day", { count: normalizedDiff.days }),
            },
            {
                value: normalizedDiff.hours,
                translated: i18n.t("common:services.time.hour", { count: normalizedDiff.hours }),
            },
            {
                value: normalizedDiff.minutes,
                translated: i18n.t("common:services.time.minute", { count: normalizedDiff.minutes }),
            },
            {
                value: normalizedDiff.seconds,
                translated: i18n.t("common:services.time.second", { count: normalizedDiff.seconds }),
            },
        ];

        const validUnits = units.filter((u) => u.value >= 1);
        if (validUnits.length === 0) {
            return null;
        }

        return validUnits
            .slice(0, 2)
            .map((u) => u.translated)
            .join(" ");
    }

    public static getHumanReadableTimestamp(timestamp: number): string {
        return this.getFromMillis(timestamp).toFormat("yyyy-MM-dd - HH:mm:ss");
    }
    public static getDaysDifferenceUntilNow(date: DateTime | string): number {
        const dateTime = typeof date === "string" ? this.getFromISO(date) : date;
        const result = dateTime.diff(this.getNow(), "days").days;
        return Math.round(result);
    }
    public static getFormattedDate(date: Date, format: "date" | "dateTime"): string {
        const dateTime = DateTime.fromJSDate(date);

        switch (format) {
            case "date":
                return dateTime.toFormat("yyyy-MM-dd");
            case "dateTime":
                return dateTime.toFormat("yyyy-MM-dd - HH:mm:ss");
            default:
                return dateTime.toFormat("yyyy-MM-dd");
        }
    }
    public static getFromISO(iso: string, fromZone?: string): DateTime {
        if (fromZone) {
            return DateTime.fromISO(iso, { zone: fromZone });
        } else {
            const hasTimezone = /([+-]\d{2}:\d{2}|Z)$/.test(iso);

            if (hasTimezone) {
                return DateTime.fromISO(iso);
            } else {
                return DateTime.fromISO(iso, { zone: "UTC" });
            }
        }
    }
    public static getFromMillis(millis: number, fromZone?: string): DateTime {
        if (fromZone) {
            return DateTime.fromMillis(millis, { zone: fromZone });
        } else {
            return DateTime.fromMillis(millis);
        }
    }
    public static getWithTimeZone(
        input: string | number | undefined | null,
        timezone: string = "UTC",
        format: string = this.dateTimeFormat,
    ): string | null {
        if (typeof input === "number") {
            return this.getFromMillis(input, timezone).toFormat(format);
        }
        if (typeof input === "string") {
            return this.getFromISO(input, timezone).toFormat(format);
        }
        return null;
    }
    public static getHumanReadableTimeDifference(
        date: DateTime | string | number,
        { zone, withAffix }: { zone?: string; withAffix?: boolean } = {},
    ): string {
        const nowInMillis = this.getNow(zone);
        const dateInMillis = this.getInMillis(date, zone);

        const timeString = this.getHumanReadableDifference(nowInMillis, dateInMillis);
        if (!timeString) {
            return i18n.t("common:services.time.now");
        }

        const pastDate = withAffix ? i18n.t("common:services.time.ago", { value: timeString }) : timeString;
        const futureDate = withAffix ? i18n.t("common:services.time.in", { value: timeString }) : timeString;

        return nowInMillis > dateInMillis ? pastDate : futureDate;
    }
    public static isDateInThePast(time: string | number): boolean {
        const date = new Date(time);
        const now = new Date();
        return date < now;
    }
    public static getLocalDate(
        input: string | number,
        type: "ISO" | "MILLIS" | "SECONDS",
        format: "date" | "dateTime" | "time",
    ): string {
        let date: DateTime;
        switch (type) {
            case "ISO":
                date = this.getFromISO(input.toString()).toLocal();
                break;
            case "MILLIS":
                date = this.getFromMillis(Number(input)).toLocal();
                break;
            case "SECONDS":
                date = this.getFromMillis(Number(input) * 1000).toLocal();
                break;
        }

        if (format === "dateTime") {
            return date.toFormat(this.dateTimeFormat);
        }
        if (format === "time") {
            return date.toFormat("HH:mm:ss");
        }
        return date.toFormat(this.dateFormat);
    }
    public static getMilisFromISO(iso: string, zone?: string): number {
        return this.getFromISO(iso, zone).toMillis();
    }
    public static getNow(zone?: string): DateTime {
        if (zone) {
            return DateTime.now().setZone(zone);
        } else {
            return DateTime.now();
        }
    }
    public static getNowInFormat(format: string, zone?: string): string {
        return this.getNow(zone).toFormat(format);
    }
    public static getNowInMillis(): number {
        return this.getNow().toMillis();
    }
    public static getStartOfDay(date: DateTime | string | number, zone?: string): string {
        let dateTime: DateTime;

        if (typeof date === "string") {
            dateTime = this.getFromISO(date, zone);
        } else if (typeof date === "number") {
            dateTime = this.getFromMillis(date, zone);
        } else {
            dateTime = date;
        }

        return dateTime.startOf("day").toString();
    }
    public static getEndOfDay(date: DateTime | string | number, zone?: string): string {
        let dateTime: DateTime;

        if (typeof date === "string") {
            dateTime = this.getFromISO(date, zone);
        } else if (typeof date === "number") {
            dateTime = this.getFromMillis(date, zone);
        } else {
            dateTime = date;
        }

        return dateTime.endOf("day").toString();
    }
    public static dateToMidnightUTC(date: Date): string {
        return DateTime.fromJSDate(date).setZone("UTC").startOf("day").toISO()!;
    }
}

export default TimeService;
